import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { colors } from "utils";
import { trackClickedButton } from "utils/analytics";
import { lcCountLabel, lcTooltipItems, questionSetLabel } from "utils/groupCardTitle";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { ChevronDownIcon, InfoIcon } from "components/pages/WarmUpPicker/icons";
import { SEPARATOR_GRAY, SelectFrame, WHITE, interText, nativeSelect } from "components/pages/WarmUpPicker/styles";
import { CheckIcon, CopyIcon } from "./warmUpCardIcons";
import { PieChartIcon } from "./oneListIcons";
import { eyebrowStyles, tagStyles } from "./OneListLessonCard";
import { QuickGroupMenu } from "./QuickGroupMenu";
import { useQuickGroupActions } from "./useQuickGroupActions";
import { ReleaseGroupCodeModal } from "./ReleaseGroupCodeModal";
import { insideStroke } from "./cardStroke";
import {
  CARD_RIGHT_PADDING,
  COLUMN_GAP,
  LEFT_COLUMN,
  RIGHT_COLUMN,
  SESSION_ROW_GAP,
  narrowLeftColumn,
  narrowMiddleColumn,
  narrowRightColumn,
  narrowRow,
  narrowSessionRow,
  useNarrow,
} from "./wrappingCardLayout";

// The Quick Group card of Manage Groups as one list (Figma 6463:7693): the
// warm-up card's horizontal layout around today's quick group behaviour.

export const quickKebabId = (quickGroupId) => `quick-card-actions-${quickGroupId}`;

// Today's quick session lengths, in whole minutes.
const QUICK_SESSION_OPTIONS = [
  { value: 10, label: "10-15 Minutes" },
  { value: 15, label: "15-20 Minutes" },
  { value: 20, label: "20-25 Minutes" },
  { value: 25, label: "25-30 Minutes" },
  { value: 30, label: "30-35 Minutes" },
];

// A stored length off the list (17.5 shows as 18) leads as its own option.
const sessionOptions = (value) =>
  QUICK_SESSION_OPTIONS.some((option) => option.value === value)
    ? QUICK_SESSION_OPTIONS
    : [{ value, label: `${value} Minutes` }, ...QUICK_SESSION_OPTIONS];

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString("en-US");
};

// Figma's bubble widths: one LC, several LCs.
const LC_TIP_WIDTH = { single: 253, several: 408 };

const LcLabel = ({ id, learningComponents }) => {
  const label = lcCountLabel(learningComponents);
  if (!label) return null;
  const items = lcTooltipItems(learningComponents);
  return (
    <Tooltip
      id={id}
      placement="right"
      arrowSize="16x12"
      maxWidth={items.length === 1 ? LC_TIP_WIDTH.single : LC_TIP_WIDTH.several}
      tip={
        <LcTipRows>
          {items.map(({ label: itemLabel, name }) => (
            <LcTipRow key={itemLabel}>
              <LcTipLabel>{itemLabel}</LcTipLabel> {name}
            </LcTipRow>
          ))}
        </LcTipRows>
      }
    >
      <LcCount tabIndex={0} aria-describedby={id}>
        {label}
      </LcCount>
    </Tooltip>
  );
};

const previewLabel = ({ question_filter: filter, question_count: count, question_set_id: setId }, setSize) =>
  questionSetLabel(filter, setId ? setSize : count, setId);

export const OneListQuickCard = ({
  quickGroup,
  classroomId,
  menuOpen,
  onMenuOpenChange,
  onPreview,
  onChanged,
  // The page's event properties around the card's own.
  eventProps,
  // Asked before Edit Group Focus leaves the page; false stays.
  guardNavigation = () => true,
  // Called once a release went through, before the list is read again.
  onArchived,
}) => {
  const code = (quickGroup.code || "").toUpperCase();
  const actions = useQuickGroupActions({
    quickGroup,
    classroomId,
    onDeleted: onChanged,
    onDuplicated: onChanged,
    onDuplicateSucceeded: (created) =>
      trackClickedButton("Duplicate Group", eventProps({ group_code: code, new_group_code: (created?.code || "").toUpperCase() })),
    onReleased: () => {
      trackClickedButton("Archive Group", eventProps({ group_code: code }));
      onArchived?.();
    },
    onSessionSaved: (sessionLength) =>
      trackClickedButton("Session Length", eventProps({ session_length: sessionLength, group_code: code, card_type: "Quick Group" })),
  });
  const { id, standard_code: standardCode, standard_name: standardName } = quickGroup;
  const cardRef = useRef(null);
  const sessionRowRef = useRef(null);
  const narrow = useNarrow(cardRef, sessionRowRef);

  const changeMenu = (open) => {
    if (open) trackClickedButton("Quick Group Menu", eventProps({ group_code: code }));
    onMenuOpenChange(open);
  };

  // Reported on the click, before any unsaved-changes prompt.
  const editGroupFocus = () => {
    trackClickedButton("Edit Group Focus", eventProps({ group_code: code, card_type: "Quick Group" }));
    if (guardNavigation()) actions.handleEdit();
  };
  const title = standardName || standardCode;
  const updated = formatDate(quickGroup.last_updated);
  const titleId = `quick-card-title-${id}`;
  const selectId = `quick-card-session-${id}`;
  return (
    <Card ref={cardRef} aria-labelledby={titleId}>
      {actions.duplicateError && <ErrorNote role="alert">{actions.duplicateError}</ErrorNote>}
      <ContentRow $narrow={narrow}>
        <LeftColumn $narrow={narrow}>
          <Tag>Quick Group</Tag>
          <HeaderBlock>
            <PieChartIcon />
            <TextColumn>
              <div>
                <EyebrowRow>
                  <Eyebrow>{standardCode}</Eyebrow>
                  <LcLabel id={`quick-card-lc-tip-${id}`} learningComponents={actions.learningComponents} />
                </EyebrowRow>
                <Title id={titleId} title={title}>
                  {title}
                </Title>
              </div>
              <Meta>
                {updated && (
                  <DateItem>
                    <span>{`Last updated: ${updated}`}</span>
                    {" "}
                    <Separator aria-hidden="true">|</Separator>
                  </DateItem>
                )}
                <PreviewLink type="button" onClick={onPreview}>
                  {previewLabel(quickGroup, actions.setSize)}
                </PreviewLink>
              </Meta>
            </TextColumn>
          </HeaderBlock>
        </LeftColumn>
        <MiddleColumn $narrow={narrow}>
          <GroupCodeLabel>Group Code:</GroupCodeLabel>
          <CodeRow>
            <Code>{code}</Code>
            <IconButton
              type="button"
              aria-label={`Copy share link for group code ${code}`}
              title={actions.copied ? "Copied!" : "Copy group code link"}
              onClick={actions.copyShareLink}
            >
              {actions.copied ? <CheckIcon /> : <CopyIcon />}
            </IconButton>
          </CodeRow>
          <SessionRow ref={sessionRowRef} $narrow={narrow}>
            <SessionLabelGroup>
              <InfoIcon stroke={colors.nightlyWoods500} />
              <SessionLabel htmlFor={selectId}>Session Length:</SessionLabel>
            </SessionLabelGroup>
            <SelectFrame>
              <SessionSelect
                id={selectId}
                value={actions.sessionValue}
                disabled={actions.savingSession}
                aria-busy={actions.savingSession ? "true" : undefined}
                onChange={(event) => actions.handleSessionChange(Number(event.target.value))}
              >
                {sessionOptions(actions.sessionValue).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </SessionSelect>
              <ChevronDownIcon stroke={colors.blue500} />
            </SelectFrame>
          </SessionRow>
        </MiddleColumn>
        <RightColumn $narrow={narrow}>
          <QuickGroupMenu
            buttonId={quickKebabId(id)}
            code={code}
            isOpen={menuOpen}
            onOpenChange={changeMenu}
            duplicating={actions.duplicating}
            onDuplicate={actions.handleDuplicate}
            onEditGroupFocus={editGroupFocus}
            onArchive={() => actions.setConfirmOpen(true)}
          />
        </RightColumn>
      </ContentRow>
      <ReleaseGroupCodeModal
        isOpen={actions.confirmOpen}
        releasing={actions.deleting}
        onClose={() => actions.setConfirmOpen(false)}
        onRelease={actions.handleRelease}
      />
    </Card>
  );
};

// No overflow clipping: the card menu must not be cut.
const Card = styled.article`
  background: ${WHITE};
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 2px 0 rgba(14, 26, 49, 0.05);
  box-sizing: border-box;
  padding: 16px ${CARD_RIGHT_PADDING}px 16px 0;
  position: relative;
  width: 100%;
  ${insideStroke(12)}
`;

const ErrorNote = styled.p`
  ${interText(14, 20, 400, "0")}
  color: #ba4a21;
  margin: 0 0 8px 24px;
`;

const ContentRow = styled.div`
  align-items: stretch;
  display: flex;
  gap: ${COLUMN_GAP}px;
  ${(p) => p.$narrow && narrowRow}
`;

const LeftColumn = styled.div`
  display: flex;
  flex: 0 0 ${LEFT_COLUMN}px;
  flex-direction: column;
  gap: 8px;
  ${(p) => p.$narrow && narrowLeftColumn}
`;

// Only this column shrinks on narrower windows; when it would be too narrow
// for its content it takes its own line under the header.
const MiddleColumn = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  ${(p) => p.$narrow && narrowMiddleColumn}
`;

const RightColumn = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 0 0 ${RIGHT_COLUMN}px;
  flex-direction: column;
  ${(p) => p.$narrow && narrowRightColumn}
`;

const Tag = styled.span`
  ${tagStyles}
  background: #bc165d;
  width: 101px;
`;

const HeaderBlock = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 24px;
`;

const TextColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const EyebrowRow = styled.div`
  align-items: baseline;
  display: flex;
  gap: 16px;
`;

const Eyebrow = styled.span`
  ${eyebrowStyles}
`;

const LcCount = styled.span`
  ${interText(12, 18, 400, "0")}
  white-space: nowrap;
`;

// One paragraph per LC, a blank 20px line between them.
const LcTipRows = styled.span`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LcTipRow = styled.p`
  margin: 0;
`;

// The page's reset sets <strong> to inherit its weight; Figma's "LC:" is bold.
const LcTipLabel = styled.strong`
  font-weight: 700;
`;

// Today's three-line clamp; the full title stays in `title`.
const Title = styled.h3`
  ${interText(18, 26, 700)}
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  margin: 0;
  max-width: 428px;
  overflow: hidden;
`;

// Figma sets the "|" one space after the date and 9px before the link.
const Meta = styled.div`
  ${interText(14, 20, 400, "0")}
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
`;

const DateItem = styled.span`
  white-space: nowrap;
`;

const Separator = styled.span`
  color: ${SEPARATOR_GRAY};
`;

const PreviewLink = styled.button`
  ${interText(14, 20, 400, "0")}
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

const boldCode = css`
  ${interText(16, 22, 700)}
  color: ${colors.nightlyWoods950};
`;

const GroupCodeLabel = styled.div`
  ${boldCode}
  margin: 0;
`;

const CodeRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const Code = styled.span`
  ${boldCode}
  white-space: nowrap;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  height: 24px;
  padding: 0;
  width: 24px;
`;

const SessionRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${SESSION_ROW_GAP}px;
  ${(p) => p.$narrow && narrowSessionRow}
`;

const SessionLabelGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const SessionLabel = styled.label`
  ${interText(14, 20, 700, "0")}
  margin: 0;
  white-space: nowrap;
`;

// Locked while saving, but drawn as usual: full opacity.
const SessionSelect = styled.select`
  ${nativeSelect(175, 36)}

  &:disabled {
    color: ${colors.nightlyWoods500};
    cursor: default;
    opacity: 1;
  }
`;
